import React from "react"
import {Trans, useTranslation} from "react-i18next"
import {twMerge} from "tailwind-merge"

import {Link} from "../../../components/Link"
import {TReachMeta} from "../../../queries/reachEngine"
import {DashboardCard} from "./DashboardCard"

const formatPercent = (ratio: number) => ratio.toLocaleString(undefined, {style: "percent", maximumFractionDigits: 1})

const OutcomeLabel: React.FC<{className?: string; children: React.ReactNode}> = ({className, children}) => (
  <div
    className={twMerge(
      "flex flex-wrap items-center gap-[7px] text-[11px] font-bold tracking-[1.1px] text-cr-slate-60 uppercase",
      className
    )}
  >
    {children}
  </div>
)

const OutcomeNote: React.FC<{className?: string; children: React.ReactNode}> = ({className, children}) => (
  <p className={twMerge("mt-[5px] text-[11.5px] text-cr-slate-60", className)}>{children}</p>
)

export const TotalResultsCard: React.FC<{results: TReachMeta["results"]}> = ({results}) => {
  const {t} = useTranslation()

  return (
    <DashboardCard title={t("ReachEngine_TotalResults_Title")} className={"px-[22px] py-[20px]"}>
      <div className={"mt-[6px] grid grid-cols-[repeat(auto-fit,minmax(230px,1fr))] items-stretch gap-[22px]"}>
        <div className={"px-[4px] py-[6px]"}>
          <OutcomeLabel>
            {t("ReachEngine_TotalResults_Sql_Label")}
            <span
              className={
                "rounded-full bg-cr-teal-light px-[7px] py-[2px] text-[9px] font-extrabold tracking-[0.8px] text-cr-teal-dark"
              }
            >
              {t("ReachEngine_TotalResults_Sql_NorthStar")}
            </span>
          </OutcomeLabel>
          <div
            className={"mt-[8px] text-[44px] leading-[1.05] font-extrabold tracking-[-1.6px] text-cr-blue tabular-nums"}
          >
            {results.sql.toLocaleString()}
          </div>
          <OutcomeNote>{t("ReachEngine_TotalResults_Sql_Note")}</OutcomeNote>
        </div>

        <div data-testid={"total-mql"} className={"border-l border-cr-slate-2 py-[6px] pr-[4px] pl-[22px]"}>
          <OutcomeLabel>{t("ReachEngine_TotalResults_Mql_Label")}</OutcomeLabel>
          <div
            className={
              "mt-[8px] text-[44px] leading-[1.05] font-extrabold tracking-[-1.6px] text-cr-slate-90 tabular-nums"
            }
          >
            {results.mql.toLocaleString()}
          </div>
          <OutcomeNote>{t("ReachEngine_TotalResults_Mql_Note")}</OutcomeNote>
        </div>

        <div
          data-testid={"sales-team-conversion"}
          className={
            "rounded-[12px] border border-dashed border-cr-slate-25 bg-cr-slate-5 px-[18px] pt-[14px] pb-[16px]"
          }
        >
          <OutcomeLabel className={"text-cr-slate-50"}>{t("ReachEngine_TotalResults_SalesTeam_Label")}</OutcomeLabel>
          <div
            className={
              "mt-[8px] text-[38px] leading-[1.05] font-extrabold tracking-[-1.4px] text-cr-slate-50 tabular-nums"
            }
          >
            {formatPercent(results.sales_team_conversion)}
          </div>
          <OutcomeNote className={"text-cr-slate-50"}>
            {t("ReachEngine_TotalResults_SalesTeam_Note", {
              won: results.won.toLocaleString(),
              sql: results.sql.toLocaleString(),
            })}
          </OutcomeNote>
          <span
            className={
              "mt-[9px] inline-block rounded-full bg-cr-slate-15 px-[7px] py-[2px] text-[9px] font-extrabold tracking-[0.7px] text-cr-slate-55 uppercase"
            }
          >
            {t("ReachEngine_TotalResults_SalesTeam_Tag")}
          </span>
          <p
            className={
              "mt-[14px] border-t border-dashed border-cr-slate-25 pt-[12px] text-[11.5px] font-semibold text-cr-blue-dark"
            }
          >
            <Trans
              i18nKey={"ReachEngine_TotalResults_SalesTeam_Klara"}
              components={{klara: <Link to={"/sales-assistant"} flipUnderline className={"font-bold"} />}}
            />
          </p>
        </div>
      </div>
    </DashboardCard>
  )
}
